/**
 * The regions: Panel (a card, or the glass surface floating over the
 * canvas), Stack (a row or column with one gap), and StatusDot (the
 * stream's liveness, pulsing while connected).
 */
import { useEffect, useRef, type CSSProperties, type ReactNode } from 'react';
import { ACTIVE_SHRINK, BORDER, CONTROL, DIM, FRAME, PAGE_COLUMN, RADIUS, SPACE, TEXT, WASH_ALPHA, tint, usePalette, type Hex, type Palette } from '../theme';

/**
 * `band` is the raised book band, `tile` a category tile (a column with a gap), `row` one record of a
 * list (a surface card, 10 round), `note` the raised strip that closes a list.
 */
export type PanelVariant = 'card' | 'glass' | 'plain' | 'band' | 'tile' | 'row' | 'note';

export function panelStyle(p: Palette, variant: PanelVariant, active = false): CSSProperties {
  if (variant === 'plain') return {};
  if (variant === 'band') {
    return { background: p.surfaceRaised, borderRadius: RADIUS.band, padding: `${SPACE.lg}px ${SPACE.wide}px`, boxSizing: 'border-box' };
  }
  if (variant === 'note') {
    return { background: p.surfaceRaised, borderRadius: RADIUS.card, padding: `${CONTROL.padYNote}px ${CONTROL.padYRow}px`, boxSizing: 'border-box' };
  }
  if (variant === 'tile' || variant === 'row') {
    return {
      background: p.surface,
      border: active ? `${BORDER.active}px solid ${p.inkStrong}` : `${BORDER.line}px solid ${p.hairline}`,
      borderRadius: RADIUS.card,
      padding: active ? `${SPACE.lg - ACTIVE_SHRINK}px ${CONTROL.padYRow - ACTIVE_SHRINK}px` : `${SPACE.lg}px ${CONTROL.padYRow}px`,
      boxSizing: 'border-box',
      ...(variant === 'tile' ? { display: 'flex', flexDirection: 'column', gap: SPACE.snug } : {}),
    };
  }
  const glass = variant === 'glass';
  return {
    background: glass ? p.surfaceGlass : p.surfaceRaised,
    backdropFilter: glass ? 'blur(16px)' : undefined,
    WebkitBackdropFilter: glass ? 'blur(16px)' : undefined,
    border: `1px solid ${p.hairline}`,
    borderRadius: glass ? RADIUS.lg : RADIUS.md,
    boxShadow: glass ? `0 25px 50px -12px ${p.shadow}` : undefined,
    padding: SPACE.xl,
    boxSizing: 'border-box',
  };
}

export function Panel({
  variant = 'card',
  children,
  width,
  role,
  ariaLabel,
  active = false,
}: {
  variant?: PanelVariant;
  children: ReactNode;
  /** A fixed width, for the centred cards of the front door. */
  width?: number;
  role?: string;
  ariaLabel?: string;
  /** A tile the walk is in: outlined in ink (the category in progress). */
  active?: boolean;
}) {
  const p = usePalette();
  return (
    <div role={role} aria-label={ariaLabel} style={{ ...panelStyle(p, variant, active), width: width ?? (variant === 'tile' ? PAGE_COLUMN.tile : undefined), maxWidth: '100%' }}>
      {children}
    </div>
  );
}

/** The centred card every front-door screen is: one Panel, FRAME.card wide. */
export function DoorCard({ children }: { children: ReactNode }) {
  const p = usePalette();
  return (
    <div
      style={{
        display: 'grid',
        placeItems: 'center',
        minHeight: '100vh',
        padding: SPACE.lg,
        boxSizing: 'border-box',
        background: p.surface,
      }}
    >
      <Panel width={FRAME.card}>{children}</Panel>
    </div>
  );
}

export type Gap = keyof typeof SPACE;

const SPACE_BY_NAME = new Map(Object.entries(SPACE));

/** The pixels a named gap spans. */
function spaceOf(gap: Gap): number {
  const pixels = SPACE_BY_NAME.get(gap);
  if (pixels === undefined) throw new Error(`no gap named ${gap} in SPACE`);
  return pixels;
}

export function Stack({
  direction = 'column',
  gap = 'sm',
  align,
  justify,
  wrap = false,
  children,
  grow = false,
  width,
}: {
  direction?: 'row' | 'column';
  gap?: Gap;
  align?: CSSProperties['alignItems'];
  justify?: CSSProperties['justifyContent'];
  wrap?: boolean;
  grow?: boolean;
  /** A fixed width in pixels (a card's action column). */
  width?: number;
  children: ReactNode;
}) {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: direction,
        gap: spaceOf(gap),
        alignItems: align,
        justifyContent: justify,
        flexWrap: wrap ? 'wrap' : undefined,
        flex: grow ? 1 : undefined,
        width,
        flexShrink: width === undefined ? undefined : 0,
        minWidth: 0,
      }}
    >
      {children}
    </div>
  );
}

/** A hairline between stacked groups. */
export function Rule() {
  const p = usePalette();
  return <hr aria-hidden="true" style={{ border: 0, borderTop: `1px solid ${p.hairline}`, margin: 0 }} />;
}

export function StatusDot({ live, label }: { live: boolean; label: ReactNode }) {
  const p = usePalette();
  const dot = useRef<HTMLSpanElement>(null);
  // The pulse is a Web Animation, not a keyframe rule: there is no
  // stylesheet. A reduced-motion preference holds the dot still.
  useEffect(() => {
    const el = dot.current;
    if (!el || !live) return;
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
    const anim = el.animate([{ opacity: 1 }, { opacity: DIM.pulse }, { opacity: 1 }], { duration: 2000, iterations: Infinity });
    return () => anim.cancel();
  }, [live]);
  const paint = live ? p.ok : p.bad;
  return (
    <span style={{ display: 'flex', alignItems: 'center', gap: SPACE.sm, fontSize: TEXT.body, color: p.inkSecondary }}>
      <span
        ref={dot}
        aria-hidden="true"
        style={{
          width: CONTROL.dot,
          height: CONTROL.dot,
          borderRadius: '50%',
          display: 'inline-block',
          flexShrink: 0,
          background: paint,
          boxShadow: `0 0 8px ${tint(paint, WASH_ALPHA.glow)}`,
        }}
      />
      {label}
    </span>
  );
}

/** A single-hue ramp: the paint from a faint wash to a strong one,
 * left to right -- the canvas's sequential scale in miniature. */
export function Ramp({ paint, from = WASH_ALPHA.whisper, to = WASH_ALPHA.rampEnd }: { paint: Hex; from?: number; to?: number }) {
  return (
    <div
      aria-hidden="true"
      style={{ height: 8, borderRadius: RADIUS.sm, background: `linear-gradient(to right, ${tint(paint, from)}, ${tint(paint, to)})` }}
    />
  );
}
